import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { syncWorkspaceFromProfile, initializeWorkspace, getWorkspaceIdFromProfile } from '@/services/workspaceInit.service';

type InitStatus = 'idle' | 'loading' | 'success' | 'error';

export function useWorkspaceInit() {
  const { user, loading: authLoading } = useAuth();
  const { activeWorkspaceId, setActiveWorkspace, isLoading, error } = useWorkspaceScope();
  
  // Wrapper for setActiveWorkspace to match old API
  const setActiveWorkspaceId = (workspaceId: string) => {
    setActiveWorkspace(workspaceId);
  };
  const initAttemptedRef = useRef(false);
  const statusRef = useRef<InitStatus>('idle');
  const [localError, setLocalError] = useState<{ message: string; code?: string } | null>(null);
  const [localLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    // Wait for auth to be ready
    if (authLoading) {
      setLocalLoading(true);
      return;
    }

    // If no user, clear workspace and stop
    if (!user) {
      setActiveWorkspaceId(null);
      setLocalLoading(false);
      setLocalError(null);
      statusRef.current = 'idle';
      initAttemptedRef.current = false;
      return;
    }

    // CRITICAL: activeWorkspaceId MUST come from profiles.workspace_id (source of truth)
    // Always sync from profile first
    const syncAndInit = async () => {
      try {
        // First, get workspace_id from profile (source of truth)
        const profileWorkspaceId = await getWorkspaceIdFromProfile(user.id);

        if (profileWorkspaceId) {
          // Profile has workspace_id - sync it to context
          if (profileWorkspaceId !== activeWorkspaceId) {
            console.log('[useWorkspaceInit] Syncing activeWorkspaceId from profile:', profileWorkspaceId);
            setActiveWorkspaceId(profileWorkspaceId);
          }
          setLocalLoading(false);
          setLocalError(null);
          statusRef.current = 'success';
          return; // Already has workspace
        }

        // No workspace in profile - need to onboard
        // Prevent multiple init attempts
        if (initAttemptedRef.current) {
          return;
        }

        // Start initialization
        initAttemptedRef.current = true;
        statusRef.current = 'loading';
        setLocalLoading(true);
        setLocalError(null);

        const doInitialize = async () => {
          const initResult = await initializeWorkspace();

          if (initResult.ok && initResult.workspace_id) {
            // Success: verify profile was updated (source of truth)
            const finalWorkspaceId = await getWorkspaceIdFromProfile(user.id);
            const workspaceIdToUse = finalWorkspaceId || initResult.workspace_id;
            
            setActiveWorkspaceId(workspaceIdToUse);
            setLocalError(null);
            statusRef.current = 'success';
            setLocalLoading(false);
            console.log('[useWorkspaceInit] Workspace initialized:', workspaceIdToUse);
          } else {
            // Error from RPC
            const errorMessage = initResult.message || initResult.error || 'Failed to initialize workspace';
            console.error('[useWorkspaceInit] RPC returned error:', errorMessage);
            setLocalError({
              message: errorMessage,
              code: initResult.error || 'ONBOARD_ERROR',
            });
            statusRef.current = 'error';
            setLocalLoading(false);
          }
        };

        doInitialize();
      } catch (err) {
        console.error('[useWorkspaceInit] Error syncing workspace from profile:', err);
        // On error, still try onboarding
        if (!initAttemptedRef.current) {
          initAttemptedRef.current = true;
          statusRef.current = 'loading';
          setLocalLoading(true);
          setLocalError(null);
          
          const initResult = await initializeWorkspace();
          if (initResult.ok && initResult.workspace_id) {
            const finalWorkspaceId = await getWorkspaceIdFromProfile(user.id);
            setActiveWorkspaceId(finalWorkspaceId || initResult.workspace_id);
            statusRef.current = 'success';
          } else {
            setLocalError({
              message: initResult.message || initResult.error || 'Failed to initialize workspace',
              code: 'INIT_ERROR',
            });
            statusRef.current = 'error';
          }
          setLocalLoading(false);
        }
      }
    };

    syncAndInit();
  }, [user, authLoading, activeWorkspaceId, setActiveWorkspaceId]);

  return {
    status: statusRef.current,
    isLoading: isLoading || authLoading || localLoading,
    error: error || localError,
    activeWorkspaceId,
  };
}

